import React, { useEffect } from 'react';

import { useDispatch } from 'react-redux';

import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import { setIsNavigationVisible, toggleNavigation } from '../../../../../store/actions/admin/content-actions';

type TUseCheckShowNavigation = (prams: {
	shouldShowControls: boolean,
	className: string
}) => boolean;

const useCheckShowNavigation: TUseCheckShowNavigation = ({
	shouldShowControls,
	className
}) => {
	const dispatch = useDispatch();
	const isNavigationVisible = useTypedSelector(state => state.ContentReducer.isNavigationVisible);
	const isNavigationOpen = useTypedSelector(state => state.ContentReducer.isNavigationOpen);

	useEffect(() => {
		const navbar = document.querySelector(`.${className}`) as HTMLElement;

		if (!shouldShowControls || !navbar) return;

		const handleMouseLeave = () => {
			if (!isNavigationOpen) {
				dispatch(setIsNavigationVisible(false));
			}
		};

		navbar.addEventListener('mouseleave', handleMouseLeave);

		return () => {
			navbar.removeEventListener('mouseleave', handleMouseLeave);
		};
	}, [className, dispatch, isNavigationOpen, shouldShowControls]);

	useEffect(() => {
		const navButton = document.querySelector('.navigation-toggle');

		if (!shouldShowControls || !navButton) return;

		const navButtonHandler = () => {
			dispatch(setIsNavigationVisible(true));
		};

		navButton.addEventListener('mouseover', navButtonHandler);

		return () => {
			navButton.removeEventListener('mouseover', navButtonHandler);
		};
	}, [dispatch, shouldShowControls]);

	return isNavigationVisible;
};

type TUseNavigationVisibility = (prams: {
	shouldShowControls: boolean,
	className: string
}) => {
	isNavigationVisible: boolean,
	handleClick: (event: React.MouseEvent<HTMLElement>) => void
};

export const useNavigationVisibility: TUseNavigationVisibility = ({ shouldShowControls, className }) => {
	const dispatch = useDispatch();
	const isNavigationOpen = useTypedSelector(state => state.ContentReducer.isNavigationOpen);

	const isNavigationVisible = useCheckShowNavigation({
		shouldShowControls,
		className
	});

	const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
		event.stopPropagation();
		dispatch(toggleNavigation(!isNavigationOpen));
	};

	useEffect(() => {
		if (isNavigationOpen) {
			const handleClick = (event: MouseEvent): void => {
				if ((event.target as Element)?.closest(`.${className}`) === null) {
					dispatch(toggleNavigation(!isNavigationOpen));
					dispatch(setIsNavigationVisible(false));
				}
			};

			document.addEventListener('click', handleClick);

			return () => {
				document.removeEventListener('click', handleClick);
			};
		}
	}, [dispatch, isNavigationOpen, isNavigationVisible, className]);

	return { isNavigationVisible, handleClick };
};
